import React from 'react';
import { motion } from 'framer-motion';
import sensorProduct from '../assets/sensor-product.png';

const Hero = () => {
    return (
        <section style={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            padding: '8rem 2rem 4rem',
        }}>
            {/* Background: Deep Organic Atmosphere */}

            {/* 1. Base Gradient (Deep Forest) */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'radial-gradient(circle at 50% 30%, #0A1F12 0%, #050A06 70%)',
                zIndex: -2
            }} />

            {/* 2. Animated "Breathing" Mist */}
            <motion.div
                animate={{ opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    position: 'absolute',
                    top: '-20%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80vw',
                    height: '80vw',
                    background: 'radial-gradient(circle, rgba(26, 77, 46, 0.4) 0%, rgba(5, 10, 6, 0) 70%)',
                    filter: 'blur(80px)',
                    zIndex: -1
                }}
            />

            {/* 3. Organic Root/Vein Network (SVG Overlay) */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                opacity: 0.15, // Subtle texture
                pointerEvents: 'none',
                overflow: 'hidden'
            }}>
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                    <defs>
                        <linearGradient id="veinGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgba(212, 255, 158, 0)" />
                            <stop offset="50%" stopColor="rgba(212, 255, 158, 0.3)" />
                            <stop offset="100%" stopColor="rgba(212, 255, 158, 0)" />
                        </linearGradient>
                    </defs>
                    {/* Generative-style organic curves (roots/mycelium) */}
                    <path d="M0,0 Q100,50 200,100 T400,200 T600,100 T800,50 T1000,150" stroke="url(#veinGrad)" strokeWidth="1" fill="none" opacity="0.5" />
                    <path d="M100,0 Q150,150 250,250 T500,300 T800,200 T1100,300" stroke="url(#veinGrad)" strokeWidth="1.5" fill="none" opacity="0.4" />
                    <path d="M-100,100 Q100,200 300,300 T600,400 T900,300" stroke="url(#veinGrad)" strokeWidth="1" fill="none" opacity="0.3" transform="scale(1.5)" />

                    {/* Vertical "growth" lines */}
                    <path d="M200,800 C250,700 150,600 200,500 S250,300 200,100" stroke="url(#veinGrad)" strokeWidth="1" fill="none" opacity="0.3" />
                    <path d="M800,800 C750,700 850,600 800,500 S750,300 800,100" stroke="url(#veinGrad)" strokeWidth="1" fill="none" opacity="0.3" />
                </svg>
            </div>

            <div style={{
                maxWidth: '1400px',
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '4rem',
                alignItems: 'center',
                zIndex: 1,
                position: 'relative'
            }}>
                {/* Left Content */}
                <div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="hero-badge"
                        style={{
                            display: 'inline-block',
                            padding: '0.5rem 1rem',
                            borderRadius: '50px',
                            border: '1px solid var(--accent-glow)',
                            color: 'var(--accent)',
                            fontSize: '0.9rem',
                            marginBottom: '2rem',
                            background: 'rgba(212, 255, 158, 0.05)'
                        }}
                    >
                        The Future of Botany
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        style={{
                            fontSize: 'clamp(3rem, 5vw, 5.5rem)',
                            lineHeight: 1.1,
                            marginBottom: '2rem',
                            fontFamily: 'Playfair Display, serif',
                        }}
                    >
                        Speak for <br />
                        <span style={{
                            color: 'transparent',
                            WebkitTextStroke: '1px var(--text-muted)',
                            fontStyle: 'italic'
                        }}>the silent.</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        style={{
                            fontSize: '1.25rem',
                            color: 'var(--text-muted)',
                            marginBottom: '3rem',
                            maxWidth: '500px',
                            lineHeight: 1.8
                        }}
                    >
                        Seikaia translates the subtle language of your plants into data you can understand.
                        Soil, light, air, and temperature—masterfully monitored.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}
                    >
                        <button style={{
                            backgroundColor: 'var(--accent)',
                            color: '#000',
                            padding: '1.2rem 2.5rem',
                            borderRadius: '0',
                            border: 'none',
                            fontSize: '1rem',
                            fontWeight: '600',
                            letterSpacing: '0.05em',
                            transition: 'transform 0.2s',
                        }}
                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            PRE-ORDER NOW
                        </button>
                        <button style={{
                            backgroundColor: 'transparent',
                            color: 'var(--text-main)',
                            padding: '1.2rem 2.5rem',
                            borderRadius: '0',
                            border: '1px solid var(--border-subtle)',
                            fontSize: '1rem',
                            fontWeight: '500',
                            letterSpacing: '0.05em'
                        }}>
                            VIEW SPECS
                        </button>
                    </motion.div>
                </div>

                {/* Right Image */}
                <motion.div
                    style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1, delay: 0.2 }}
                >
                    {/* Image Glow/Halo */}
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '60%',
                        height: '60%',
                        background: 'var(--accent)',
                        filter: 'blur(120px)',
                        opacity: 0.15,
                        zIndex: -1
                    }} />

                    <img
                        src={sensorProduct}
                        alt="Seikaia Sensor"
                        style={{
                            width: '100%',
                            maxWidth: '600px', // Limit max width
                            height: 'auto',
                            maxHeight: '80vh', // Keep within viewport mostly
                            objectFit: 'contain',
                        }}
                    />
                </motion.div>
            </div>

            {/* Responsive Styles Injection */}
            <style>{`
        @media (max-width: 900px) {
          section > div { grid-template-columns: 1fr !important; text-align: center; }
          h1 { font-size: 3rem !important; }
          .hero-badge { margin: 0 auto 2rem !important; }
          p { margin: 0 auto 3rem !important; }
          div[style*="flexWrap"] { justify-content: center; }
        }
      `}</style>
        </section>
    );
};

export default Hero;
